import React, { useState, useEffect, useContext, useMemo, lazy } from "react";
import styled from "styled-components";
import BqnContext from "common/BqnContext";
import ActionsContext from "common/ActionsContext";
import useProfile from "common/hooks/useProfile";
import { getBqnIdCmdPart, safeStr, getIPv6Prefix } from "common/api";
import { getThresholds } from "../../Subscriber&Groups/utils";
import { getQuantileStats } from "common/api";
import { getTargets } from "./TimeEvolutionMetrics/utils";
import LazyInView from "common/LazyInView";

const TimeEvolutionMetrics = lazy(() => import("./TimeEvolutionMetrics"));
const ApplicationSpeed = lazy(() => import("../../Subscriber&Groups/ApplicationSpeed"));
const Statistics = lazy(() => import("./Statistics"));
const ListSubscribers = lazy(() => import("./ListSubscribers"));

const TimeEvolutionMetricsWrapper = styled.div`
  min-height: 1000px;
`;

const EmptyChart = styled.div`
  width: 100%;
  height: 50px;
`;

const SUBSCRIBERS_TO_PLOT_LIMIT = 50;

const subsToPlotFirst = 25;
const BOXPLOT_METRICS_SET = new Set([
  "MAX-Mbps",
  "RTT-P80-ms",
  "RTT-MED-ms",
  "RTT-MIN-ms",
  "RTX",
  "CONGEST",
  "MAX-SPEED",
]);
const RETURN_TO_VIEW = "viewStatusSubscribers";

const addCIDR = (subnet) =>
  subnet.includes(":")
    ? /*ipv6*/ getIPv6Prefix().then((prefix) => `${subnet}/${prefix}`)
    : `${subnet}/32`;

const ensureCIDR = (subnet) =>
  Promise.resolve(
    subnet.length === 0 || subnet.includes("/") ? subnet : addCIDR(subnet)
  );

const statePolicy = (policy) =>
  policy === "all" ? "" : `policy ${safeStr(policy)}`;

const stateSubscriber = (subnet) =>
  "subscriber " + (subnet.length === 0 ? "all" : subnet);

const stateSubscriberGroup = (subscriberGroup) =>
  subscriberGroup && subscriberGroup !== "all"
    ? `subscriber-group ${safeStr(subscriberGroup)}`
    : "";

const responseMustContainAtLeastOneRecord = (subnet) => (response) => {
  if (subnet.length > 0) {
    const [_head, ...rows] = response.trim("\n").split("\n");
    if (rows === undefined || rows.length < 1) {
      throw `%ERR-ENOENT: Cannot find subscriber with ip ${subnet}`;
    }
  }

  return { response };
};

const createPlaceholder = ({ addr, bqnId, mode }) => {
  if (mode === "standalone") {
    return [
      "ADDR            SUBSCRIBER-ID     BLOCK      FL-ACTIVE FL-CREATED   BYTES-UPLINK BYTES-DOWNLINK  CURR-Mbps  MEAN-Mbps   MAX-Mbps   LIM-Mbps    RTT-ms      RTX POLICY-RATE              MAX-SPEED  CONGEST   WARN   LIFETIME",
      `${addr}         n/a               n/a        0         n/a                n/a            n/a     n/a        n/a        n/a        n/a       n/a    0.00%  n/a                 0.00%    0.00%   0   0:00:00`,
    ].join("\n");
  }

  return [
    "BQN   ADDR            SUBSCRIBER-ID     BLOCK      FL-ACTIVE FL-CREATED   BYTES-UPLINK BYTES-DOWNLINK  CURR-Mbps  MEAN-Mbps   MAX-Mbps   LIM-Mbps    RTT-ms      RTX POLICY-RATE              MAX-SPEED  CONGEST   WARN   LIFETIME",
    `${bqnId}   ${addr}         n/a               n/a        0         n/a                n/a            n/a     n/a        n/a        n/a        n/a       n/a    0.00%  n/a                 0.00%    0.00%   0   0:00:00`,
  ].join("\n");
};

const ensurePlaceholder =
  ({ subnet, bqnId, mode }) =>
  (error) => {
    if (error.startsWith("%ERR-ENOENT: Cannot find subscriber with")) {
      const addr = subnet.replace(/\/.*$/, "");
      return { response: createPlaceholder({ addr, bqnId, mode }) };
    }
    throw error;
  };

const doSubscriberMetricsRequest = ({
  policy,
  subnet,
  direction,
  lines,
  subscriberGroup,
  bqnId,
  mode,
}) =>
  ensureCIDR(subnet)
    .then((cidrSubnet) => {
      return ifCl.run(
        `show ${statePolicy(policy)} ${stateSubscriber(
          cidrSubnet
        )} ${direction} ${stateSubscriberGroup(
          subscriberGroup
        )} lines ${lines}${getBqnIdCmdPart(mode, bqnId)}`
      );
    })
    .then(responseMustContainAtLeastOneRecord(subnet))
    .catch(ensurePlaceholder({ subnet, bqnId, mode }));
    
const getSubscribersToPlot = (data, selected) => {
  const dataById = data.reduce((acc, row) => {
    acc[row.id] = row;
    return acc;
  }, {});
  return selected.map((id) => dataById[id]).filter((row) => row);
};

export const openSubscriberDetails = (target, timePeriod, returnView = RETURN_TO_VIEW) => {
  globalNavigate("viewStatusSubscribers", {
    ...target,
    returnView,
    timePeriod,
  });
};

const Report = ({
  policy,
  subnet,
  direction,
  lines,
  subscriberGroup,
  timePeriod,
  metric,
  ...otherSettings
}) => {
  const [tableData, setTableData] = useState([]);
  const [selected, setSelected] = useState([]);
  const [thresholds, setThresholds] = useState({});
  const [statisticsData, setStatisticsData] = useState({});
  const [colorize, setColorize] = useState(false);
  const [isFirstSelection, setIsFirstSelection] = useState(true);
  const [selMetric, setSelMetric] = useState(metric || "averageSpeed");
  const [selTimePeriod, setSelTimePeriod] = useState(timePeriod || "oneDay");
  const [entity, setEntity] = useState("ip-address");
  const [meanSorting, setMeanSorting] = useState("default");
  const [fieldsMeanMap, setFieldsMeanMap] = useState({});
  const profile = useProfile() || {};

  const actions = useContext(ActionsContext);
  const {
    clusterStatus: { mode },
    bqnId,
  } = useContext(BqnContext);

  const resetData = () => {
    setTableData([]);
    setSelected([]);
    setStatisticsData({});
    setThresholds({});
  };

  useEffect(() => {
    resetData();
  }, [bqnId]);

  useEffect(() => {
    doLoad();
    return actions.recv("do-load", () => {
      resetData();
      doLoad();
    });
  }, [policy, subnet, direction, lines, subscriberGroup, bqnId]);


  const doLoad = () => {
    Promise.all([
      doSubscriberMetricsRequest({
        policy,
        subnet,
        direction,
        lines,
        subscriberGroup,
        mode,
        bqnId,
      }),
      getThresholds(),
    ])
      .then(([{ response: responseMetrics }, responseThresholds]) => {
        const [head, ...rows] = responseMetrics.trim("\n").split("\n");
        const headerAttrs = head.trim().split(/\s+/);
        const headerMap = headerAttrs.reduce((acc, attr, index) => {
          acc[index] = attr;
          return acc;
        }, {});
        const parsedData = rows.reduce(
          (acc, row, indexRow) => {
            const attrs = row.trim().split(/\s+/);
            const item = attrs.reduce(
              (accItem, attr, index) => {
                const attrName = headerMap[index];
                accItem[attrName] = attr;
                if (BOXPLOT_METRICS_SET.has(attrName)) {
                  if (acc[attrName]) {
                    acc[attrName].push(attr);
                  } else {
                    acc[attrName] = [attr];
                  }
                }
                return accItem;
              },
              { id: indexRow }
            );
            acc.rows.push(item);
            return acc;
          },
          { rows: [] }
        );
        setStatisticsData(parsedData);
        setTableData(parsedData.rows);
        const nrSelected =
          parsedData.rows.length < subsToPlotFirst
            ? parsedData.rows.length
            : subsToPlotFirst;
        const firstSelection = Array.from(Array(nrSelected).keys());
        setSelected(firstSelection);
        setThresholds(responseThresholds);
      })
      .catch((error) => {
        showModalError("Error", error);
      });
  };

  const subscribersToPlot = useMemo(
    () => getSubscribersToPlot(tableData, selected),
    [tableData, selected]
  );

  const targets = useMemo(() => {
    try {
      return getTargets(entity, subscribersToPlot);
    } catch (error) {
      console.error("Failed to get targets:", error);
      return [];
    }
  }, [subscribersToPlot, entity]);

  const metricsQuantileData = useMemo(() => {
    return Object.entries(statisticsData).reduce((acc, [key, values]) => {
      const threshold = key === "RTT-P80-ms" ? thresholds["RTT-80%-ms"] : thresholds[key];
      const custom = threshold === "auto" ? thresholds["PERCENT-WARNING"]/100 : undefined;
      if (key !== "rows") {
        const validValues = values.reduce((accValues, value) => {
          if (value !== "n/a" && value !== null) {
            accValues.push(parseFloat(value));
          }
          return accValues;
        }, []);
        const quantileData = getQuantileStats(validValues, custom);
        acc[key] = quantileData;
      }
      return acc;
    }, {});
  }, [statisticsData, thresholds]);

  return (
    <>
      <TimeEvolutionMetricsWrapper>
        <LazyInView>
          <TimeEvolutionMetrics
            direction={direction}
            subscribersToPlot={subscribersToPlot}
            timePeriod={selTimePeriod}
            setTimePeriod={setSelTimePeriod}
            metric={metric}
            mode={mode}
            bqnId={bqnId}
            isFirstSelection={isFirstSelection}
            selMetric={selMetric}
            setSelMetric={setSelMetric}
            entity={entity}
            setEntity={setEntity}
            meanSorting={meanSorting}
            setMeanSorting={setMeanSorting}
            setFieldsMeanMap={setFieldsMeanMap}
          />
        </LazyInView>
      </TimeEvolutionMetricsWrapper>
      {profile && profile.dpiEnabled === true ? (
        <LazyInView rootMargin="0px">
          <ApplicationSpeed
            direction={direction}
            entity={entity}
            setEntity={setEntity}
            targets={targets}
            isFirstSelection={isFirstSelection}
            mode={mode}
            bqnId={bqnId}
            onContextClick={(sub) => {
              const target =
                entity === "ip-address" ? { addr: sub } : { subsId: sub };
              openSubscriberDetails(target, selTimePeriod);
            }}
          />
        </LazyInView>
      ) : null}
      <LazyInView>
        <Statistics
          {...otherSettings}
          data={statisticsData}
          thresholds={thresholds}
          setThresholds={setThresholds}
          colorize={colorize}
          setColorize={setColorize}
          metricsQuantileData={metricsQuantileData}
        />
      </LazyInView>
      <LazyInView>
        <ListSubscribers
          tableData={tableData}
          colorize={colorize}
          selected={selected}
          setSelected={(value) => {
            setIsFirstSelection(false);
            setSelected(value);
          }}
          thresholds={thresholds}
          meanSorting={meanSorting}
          fieldsMeanMap={fieldsMeanMap}
          metricsQuantileData={metricsQuantileData}
        />
      </LazyInView>
    </>
  );
};

export default Report;
